import {
  DEFAULT_CURRENCY,
  ORGANIZATION,
  SITE_NAME,
  SITE_URL,
  buildCanonical,
} from "./config";

export const organizationLd = () => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  name: ORGANIZATION.name,
  url: ORGANIZATION.url,
  logo: ORGANIZATION.logo,
  sameAs: ORGANIZATION.sameAs,
  contactPoint: ORGANIZATION.contactPoint.map((cp) => ({
    "@type": "ContactPoint",
    ...cp,
  })),
  address: ORGANIZATION.address,
});

export const websiteLd = () => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: SITE_NAME,
  url: SITE_URL,
  potentialAction: {
    "@type": "SearchAction",
    target: `${SITE_URL}/products?search={search_term_string}`,
    "query-input": "required name=search_term_string",
  },
});

interface ProductLdInput {
  id: number | string;
  name: string;
  description?: string;
  image: string | string[];
  brandName?: string;
  price: number;
  currency?: string;
  inStock: boolean;
  averageRating?: number;
  reviewCount?: number;
  sku?: string | number;
}

export const productLd = (p: ProductLdInput) => {
  const offer: Record<string, unknown> = {
    "@type": "Offer",
    url: buildCanonical(`/product/${p.id}`),
    priceCurrency: p.currency ?? DEFAULT_CURRENCY,
    price: p.price.toFixed(2),
    availability: p.inStock
      ? "https://schema.org/InStock"
      : "https://schema.org/OutOfStock",
    itemCondition: "https://schema.org/NewCondition",
  };

  const ld: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: p.name,
    description: p.description ?? "",
    image: p.image,
    sku: String(p.sku ?? p.id),
    brand: { "@type": "Brand", name: p.brandName || SITE_NAME },
    offers: offer,
  };

  if (p.averageRating && p.reviewCount && p.reviewCount > 0) {
    ld.aggregateRating = {
      "@type": "AggregateRating",
      ratingValue: p.averageRating.toFixed(1),
      reviewCount: p.reviewCount,
    };
  }

  return ld;
};

interface BreadcrumbItem {
  name: string;
  path: string;
}

export const breadcrumbLd = (items: BreadcrumbItem[]) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((item, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: item.name,
    item: buildCanonical(item.path),
  })),
});
